import type {AxiosError} from 'axios';
import axios from 'axios';
import {Form} from '@/plugins/FWForms/index';
import * as types from '../mutation-types';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {VehicleClass, VehicleMake, VehicleModel} from '@/types/vehicle.ts';
import {push} from 'notivue';
import {handleApiError} from '@/utils/errorHandler.ts';

interface VehicleStatesResponse {
    states: string[];
}

export type VehicleState = {
    loading: Record<string, boolean>,
    vehicleClassCreated: boolean,
    vehicleClassesLoaded: boolean,
    vehicleClassForm: Form<any>,
    vehicleMakes: VehicleMake[],
    vehicleModels: VehicleModel[],
    vehicleClasses: VehicleClass[],
    vehicleClass: VehicleClass | null,
}

const state: VehicleState = {
    loading: {
        makes: false,
        models: false,
        fetchOdometer: false,
        classes: false,
        class: false,
    },
    vehicleClassCreated: false,
    vehicleClassesLoaded: false,
    vehicleClassForm: new Form(),
    vehicleMakes: [],
    vehicleModels: [],
    vehicleClasses: [],
    vehicleClass: null,
};

const getters = {
    vehicleMakes: (state: VehicleState) => state.vehicleMakes,
    vehicleModels: (state: VehicleState) => state.vehicleModels,
    vehicleLoading: (state: VehicleState) => state.loading,
    vehicleClasses: (state: VehicleState) => state.vehicleClasses,
    vehicleClass: (state: VehicleState) => state.vehicleClass,
    vehicleClassForm: (state: VehicleState) => state.vehicleClassForm,
    vehicleClassCreated: (state: VehicleState) => state.vehicleClassCreated,
};

const mutations = {
    [types.SET_VEHICLES_LOADING](state: VehicleState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_VEHICLE_MAKES](state: VehicleState, makes: VehicleMake[]) {
        state.vehicleMakes = makes;
    },
    [types.SET_VEHICLE_MODELS](state: VehicleState, models: VehicleModel[]) {
        state.vehicleModels = models ?? [];
    },
    [types.SET_VEHICLE_CLASS_CREATED](state: VehicleState, value: boolean) {
        state.vehicleClassCreated = value;
    },
    [types.SET_VEHICLE_CLASSES](state: VehicleState, classes: VehicleClass[]) {
        state.vehicleClassesLoaded = true;
        state.vehicleClasses = classes;
    },
    [types.SET_VEHICLE_CLASS](state: VehicleState, vehicleClass: VehicleClass) {
        state.vehicleClass = vehicleClass;
    },
    [types.SET_VEHICLE_FORM_ERRORS](state: VehicleState, errors: any) {
        state.vehicleClassForm.errors.set(errors);
    },
    [types.CLEAR_VEHICLE_FORM_ERRORS](state: VehicleState, form_field: string | null) {
        if (form_field === null || form_field === undefined) {
            state.vehicleClassForm.errors.clear();
        } else {
            state.vehicleClassForm.errors.clear(form_field);
        }
    },
};

const actions = {

    /**
     * Fetch vehicle makes
     */
    async fetchVehicleMakes({commit}, refresh = false) {
        if (state.vehicleMakes.length > 0 && !refresh) {
            return;
        }

        const responseData = await performApiRequest(commit, 'SET_VEHICLES_LOADING', 'makes', () => axios.get('/v1/vehicles/makes'));
        commit(types.SET_VEHICLE_MAKES, responseData.makes);
    },

    /**
     * Fetch vehicle models
     */
    async fetchVehicleModels({commit}, make: string) {
        const responseData = await performApiRequest(commit, 'SET_VEHICLES_LOADING', 'models', () => axios.get(`/v1/vehicles/makes/${make}/models`));
        commit(types.SET_VEHICLE_MODELS, responseData?.models ?? []);
    },


    /**
     * Get vehicle classes
     */
    async getVehicleClasses({commit}, refresh = false) {
        if (state.vehicleClassesLoaded && !refresh) {
            return;
        }

        const responseData = await performApiRequest(commit, 'SET_VEHICLES_LOADING', 'classes', () => axios.get('/v1/vehicle-classes'));
        commit(types.SET_VEHICLE_CLASSES, responseData.classes);
    },


    /**
     * Get vehicle class
     */
    async getClass({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_VEHICLES_LOADING', 'class', () => axios.get(`/v1/vehicle-classes/${uuid}`));
            commit('SET_VEHICLE_CLASS', responseData.class);
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Create a vehicle class
     */
    async createClass({commit, dispatch}, vehicleClass: VehicleClass) {
        try {
            await performApiRequest(commit, 'SET_VEHICLES_LOADING', 'class', () => axios.post('/v1/vehicle-classes', vehicleClass));
            push.success({
                message: 'Vehicle class created successfully!',
            });
            commit('SET_VEHICLE_CLASS_CREATED', true);
            dispatch('getVehicleClasses', true);
        } catch (error) {
            const err = error as AxiosError;
            const errors = (err?.response?.data as {errors?: Record<string, string[]>})?.errors;

            if (errors) {
                commit('SET_VEHICLE_FORM_ERRORS', errors);
                return;
            }

            handleApiError(error, 'There was an error creating the vehicle class.');
        }
    },


    /**
     * Update a vehicle class
     */
    async updateClass({commit, dispatch}, vehicleClass: VehicleClass) {
        try {
            const responseData = await performApiRequest(commit, 'SET_VEHICLES_LOADING', 'class', () => axios.put(`/v1/vehicle-classes/${vehicleClass.uuid}`, vehicleClass));
            commit(types.SET_VEHICLE_CLASS, responseData.class);
            dispatch('getVehicleClasses', true);

            push.success({
                message: 'Vehicle class saved successfully!',
            });
        } catch (error) {
            const err = error as AxiosError;
            const errors = (err?.response?.data as { errors: Record<string, string[]> })?.errors;

            if (errors) {
                state.vehicleClassForm.errors.set(errors);
            }

            push.error({
                message: 'There was an error saving the vehicle class.',
            });
        }
    },


    /**
     * Create a vehicle class
     */
    async deleteClass({commit}, uuid: string) {
        try {
            return await performApiRequest(commit, 'SET_VEHICLES_LOADING', 'class', () => axios.delete(`/v1/vehicle-classes/${uuid}`));
        } catch (error) {
            handleApiError(error);
            throw error;
        }
    },

    async unassignVehiclesFromClass({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_VEHICLES_LOADING', 'class', () => axios.post(`/v1/vehicle-classes/${uuid}/unassign-vehicles`));
            commit(types.SET_VEHICLE_CLASS, responseData.class);
            return responseData;
        } catch (error) {
            handleApiError(error);
            throw error;
        }
    },


    /**
     * Get a vehicle state
     */
    async getStates({commit}, v_id: string) {
        try {
            const responseData = await performApiRequest<VehicleStatesResponse>(commit, 'SET_VEHICLES_LOADING', 'getStates', () => axios.get(`/v1/vehicles/${v_id}/states`));
            return responseData.states;
        } catch (error) {
            handleApiError(error);
        }
    },


    /**
     * Fetch a vehicle odometer
     */
    async fetchOdometer({commit}, v_id: string): Promise<{ vehicle: any, states: any[] } | undefined> {
        try {
            const responseData = await performApiRequest(commit, 'SET_VEHICLES_LOADING', 'fetchOdometer', () => axios.get(`/v1/vehicles/${v_id}/odometer`));

            // states: the refreshed reading history, so "Last recording" can update too.
            return {vehicle: responseData.vehicle, states: responseData.states ?? []};
        } catch (error) {
            handleApiError(error);

            return undefined;
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
